// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import React, {useContext, useEffect, useRef, useState} from 'react';

import {ArrowNavigationContext} from './arrow-navigation-context';

import type {Item} from './typedef';

const FocusableTabIndex = 0;
const NonFocusableTabIndex = -1;

type Return = {
    tabIndex: number,
    ref: React.RefObject<HTMLElement | null>,
    addSelf: () => void,
    removeSelf: () => void,
    setActive: () => void,
};

/**
 * Registers a single navigable item within an `ArrowNavigationContext` and
 * manages its `tabIndex` based on whether it is the currently focused item.
 *
 * Must be used inside a component tree wrapped by `ArrowNavigationContext.Provider`
 * (provided by `useArrowNavigation`). The item registers itself on mount and
 * deregisters on unmount automatically.
 *
 * The returned `ref` must be attached to the item's DOM element so the parent
 * hook can read its DOM position and programmatically focus it.
 *
 * @returns An object with:
 *   - `ref` — attach to the item's root DOM element.
 *   - `tabIndex` — `0` when this item is the active/pointed item, `-1` otherwise
 *     (implements the roving tabindex pattern).
 *   - `addSelf()` — re-registers the item (e.g. after a conditional unmount/remount).
 *   - `removeSelf()` — deregisters the item from the navigation group.
 *   - `setActive()` — imperatively marks this item as the active navigation target.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/utils-arrow-navigation--documentation}
 */
export const useArrowNavigationItem = (): Return => {
    const [isPointed, setPointed] = useState(false);

    const ref = useRef<HTMLElement>(null);
    const self = useRef<Item>({setPointed, ref});
    const {addItem, removeItem, setActiveItem} = useContext(ArrowNavigationContext);

    useEffect(() => {
        const {current} = self;
        addItem(current);
        return () => removeItem(current);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return {
        tabIndex: isPointed ? FocusableTabIndex : NonFocusableTabIndex,
        ref,
        addSelf: () => addItem(self.current),
        removeSelf: () => removeItem(self.current),
        setActive: () => setActiveItem(self.current),
    };
};
